import React, { useState, useCallback, useMemo } from 'react';
import MonthlyHeader from './MonthlyHeader';
import useSelectedSensor from '../../hooks/useSelectedSensor';
import useSensorOptions from '../../hooks/useSensorOptions';
import useCompanyOptions from '../../hooks/useCompanyOptions';
import useSelectedCompany from '../../hooks/useSelectedCompany';
import MonthlyContent from './MonthlyContent';
import { Box, Grid, Paper, useTheme, useMediaQuery } from '@mui/material';
import { useMonthlyDataQuery, useAdultnChildMonthlyDataQuery, useGenderMonthlyDataQuery, useGetAddonByDeviceQuery } from '../../services/api';
import MonthlyFilterBox from './MonthlyFilterBox';
import MonthlyLabel from './MonthlyLabel';
import { useSelector } from 'react-redux';
import useGroupOptions from '../../hooks/useGroupOptions';
import useSelectedGroup from '../../hooks/useSelectedGroup';

const Monthly = () => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { company, role } = useSelector((state) => state.global);
    const isSuperOrPowerAdmin = role === 'Super Admin' || role === 'Power Admin';

    // Company, Device and group options
    const { companyOptions } = useCompanyOptions();
    const { selectedCompany, selectedCompanyId, handleCompanyChange } = useSelectedCompany({ companyOptions });
    const groupOptions = useGroupOptions(isSuperOrPowerAdmin ? selectedCompanyId : company);
    const sensorOptions = useSensorOptions(isSuperOrPowerAdmin ? selectedCompanyId : company);
    const { selectedGroup, handleGroupChange } = useSelectedGroup(groupOptions, isSuperOrPowerAdmin ? selectedCompanyId : company);
    const filteredSensorOptions = useMemo(() => {
        if (!groupOptions.length) {
            return sensorOptions;
        }

        const selectedGroupObj = groupOptions.find((group) => group._id === selectedGroup);
        const selectedDeviceIds = selectedGroupObj?.deviceIds || [];

        return sensorOptions.filter((sensor) => selectedDeviceIds.includes(sensor._id));
    }, [sensorOptions, groupOptions, selectedGroup]);
    const { selectedSensor, handleSensorChange, getSelectedSensorLabel } = useSelectedSensor(filteredSensorOptions, isSuperOrPowerAdmin ? selectedCompany : company);

    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedYearRange, setSelectedYearRange] = useState(getDefaultYearRange());
    const [showLabels, setShowLabels] = useState(true);
    const [tabValue, setTabValue] = useState(0);

    // API
    const { data: addonData } = useGetAddonByDeviceQuery(selectedSensor, { skip: !selectedSensor });
    const { data: monthlyData, isLoading: monthlyLoading, error: monthlyError } = useMonthlyDataQuery({ mac: selectedSensor }, { skip: tabValue !== 0 || !selectedSensor });
    const { data: adultChildData, isLoading: adultChildLoading, error: adultChildError } = useAdultnChildMonthlyDataQuery({ mac: selectedSensor }, { skip: tabValue !== 1 || !selectedSensor });
    const { data: genderData, isLoading: genderLoading, error: genderError } = useGenderMonthlyDataQuery({ mac: selectedSensor }, { skip: tabValue !== 2 || !selectedSensor });

    const processedData = tabValue === 0 ? monthlyData : tabValue === 1 ? adultChildData : tabValue === 2 ? genderData : null;
    const chartIsLoading = tabValue === 0 ? monthlyLoading : tabValue === 1 ? adultChildLoading : tabValue === 2 ? genderLoading : null;
    const error = tabValue === 0 ? monthlyError : tabValue === 1 ? adultChildError : tabValue === 2 ? genderError : null;

    const handlePopoverOpen = useCallback((event) => setAnchorEl(event.currentTarget), []);
    const handlePopoverClose = useCallback(() => setAnchorEl(null), []);

    function getDefaultYearRange() {
        const currentYear = new Date().getFullYear();
        return [currentYear];
    }

    const handleShowLabelsChange = useCallback((event) => {
        setShowLabels(event.target.checked);
    }, []);

    // Handle tab change
    const handleTabChange = (newValue) => {
        setTabValue(newValue);
    };

    return (
        <Box m={isSmallScreen ? '0.5rem' : '1rem 2.5rem'}>
            {isSmallScreen ? (
                // Mobile view
                <Grid container spacing={2} paddingBottom={3}>
                    <Grid item xs={12}>
                        <MonthlyHeader isSmallScreen={isSmallScreen} selectedYearRange={selectedYearRange} />
                        <MonthlyLabel sensorLabel={getSelectedSensorLabel()} isSmallScreen={isSmallScreen} tabValue={tabValue} onTabChange={handleTabChange} addonData={addonData} />
                    </Grid>
                    <Grid item xs={12}>
                        <Paper
                            sx={{
                                position: 'sticky',
                                top: 20,
                                height: 'auto',
                                overflow: 'auto',
                                borderRadius: '15px',
                                boxShadow: '8px 8px 20px rgba(0, 0, 0, 0.1)',
                            }}
                        >
                            <MonthlyFilterBox
                                sensorLabel={getSelectedSensorLabel()}
                                isSmallScreen={isSmallScreen}
                                showLabels={showLabels}
                                onShowLabelsChange={handleShowLabelsChange}
                                sensorOptions={filteredSensorOptions}
                                selectedSensor={selectedSensor}
                                selectedYearRange={selectedYearRange}
                                setSelectedYearRange={setSelectedYearRange}
                                handleSensorChange={handleSensorChange}
                                handlePopoverOpen={handlePopoverOpen}
                                handlePopoverClose={handlePopoverClose}
                                anchorEl={anchorEl}
                                companyOptions={companyOptions}
                                selectedCompany={isSuperOrPowerAdmin ? selectedCompany : null}
                                handleCompanyChange={isSuperOrPowerAdmin ? handleCompanyChange : null}
                                isSuperAdmin={isSuperOrPowerAdmin}
                                selectedGroup={selectedGroup}
                                handleGroupChange={handleGroupChange}
                                groupOptions={groupOptions}
                            />
                        </Paper>
                    </Grid>
                    <Grid item xs={12}>
                        <MonthlyContent
                            selectedSensor={selectedSensor}
                            selectedYearRange={selectedYearRange}
                            showLabels={showLabels}
                            processedData={processedData}
                            chartIsLoading={chartIsLoading}
                            error={error}
                            isSmallScreen={isSmallScreen}
                            sensorLabel={getSelectedSensorLabel()}
                            tabValue={tabValue}
                        />
                    </Grid>
                </Grid>
            ) : (
                // Web view
                <Grid container spacing={2} paddingBottom={3}>
                    <Grid item xs={12} md={10}>
                        <MonthlyHeader isSmallScreen={isSmallScreen} selectedYearRange={selectedYearRange} />
                        <MonthlyLabel sensorLabel={getSelectedSensorLabel()} isSmallScreen={isSmallScreen} tabValue={tabValue} onTabChange={handleTabChange} addonData={addonData} />
                        <MonthlyContent
                            selectedSensor={selectedSensor}
                            selectedYearRange={selectedYearRange}
                            showLabels={showLabels}
                            processedData={processedData}
                            chartIsLoading={chartIsLoading}
                            error={error}
                            isSmallScreen={isSmallScreen}
                            sensorLabel={getSelectedSensorLabel()}
                            tabValue={tabValue}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Paper
                            sx={{
                                position: 'sticky',
                                top: 20,
                                height: 'auto',
                                overflow: 'auto',
                                borderRadius: '15px',
                                boxShadow: '8px 8px 20px rgba(0, 0, 0, 0.1)',
                            }}
                        >
                            <MonthlyFilterBox
                                sensorLabel={getSelectedSensorLabel()}
                                isSmallScreen={isSmallScreen}
                                showLabels={showLabels}
                                onShowLabelsChange={handleShowLabelsChange}
                                sensorOptions={filteredSensorOptions}
                                selectedSensor={selectedSensor}
                                selectedYearRange={selectedYearRange}
                                setSelectedYearRange={setSelectedYearRange}
                                handleSensorChange={handleSensorChange}
                                handlePopoverOpen={handlePopoverOpen}
                                handlePopoverClose={handlePopoverClose}
                                anchorEl={anchorEl}
                                companyOptions={companyOptions}
                                selectedCompany={isSuperOrPowerAdmin ? selectedCompany : null}
                                handleCompanyChange={isSuperOrPowerAdmin ? handleCompanyChange : null}
                                isSuperAdmin={isSuperOrPowerAdmin}
                                selectedGroup={selectedGroup}
                                handleGroupChange={handleGroupChange}
                                groupOptions={groupOptions}
                            />
                        </Paper>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
};

export default Monthly;
